import { Star, Sparkles } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { STATUSES, STATUS_LABELS } from '../constants';
import type { Lead } from '../types';

interface LeadsKanbanProps {
  leads: Lead[];
  isLoading: boolean;
  onRowClick: (id: string) => void;
  onStatusChange: (id: string, newStatus: string) => void;
}

function scoreTone(score: number): string {
  if (score >= 80) return 'bg-success/10 text-success';
  if (score >= 50) return 'bg-warning/10 text-warning';
  return 'bg-danger/10 text-danger';
}

export function LeadsKanban({ leads, isLoading, onRowClick, onStatusChange }: LeadsKanbanProps) {
  if (isLoading) {
    return (
      <div className="flex gap-4 py-2">
        {STATUSES.map((s) => (
          <div key={s} className="w-72 shrink-0 space-y-3">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    );
  }

  const cols: Record<string, Lead[]> = { new: [], contacted: [], interested: [], converted: [], rejected: [] };
  leads.forEach((lead) => {
    if (cols[lead.status]) cols[lead.status].push(lead);
  });

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 pt-2">
      {STATUSES.map((status) => (
        <div key={status} className="flex w-72 shrink-0 flex-col gap-3 rounded-card border border-outline bg-surface-container-low p-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-label-caps uppercase tracking-wider text-on-surface-variant">
              {STATUS_LABELS[status]}
            </h3>
            <span className="rounded-full border border-outline bg-surface px-2 py-0.5 text-label-caps font-semibold text-on-surface-variant">
              {cols[status].length}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {cols[status].length === 0 ? (
              <div className="flex h-16 items-center justify-center rounded-card-sm border border-dashed border-outline text-body-sm text-on-surface-variant">
                No leads
              </div>
            ) : (
              cols[status].map((lead) => (
                <div
                  key={lead.id}
                  className="group flex cursor-pointer flex-col gap-2 rounded-card-sm border border-outline bg-surface p-3 transition-colors hover:border-primary/40"
                  onClick={() => onRowClick(lead.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate text-data-md font-semibold text-on-surface" title={lead.name}>
                      {lead.name}
                    </div>
                    {lead.ai_score != null && (
                      <div className={cn('flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-label-caps font-bold', scoreTone(lead.ai_score))}>
                        <Sparkles className="h-3 w-3" /> {lead.ai_score}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-body-sm text-on-surface-variant">
                    {parseFloat(lead.rating || '0') > 0 && (
                      <span className="flex items-center gap-0.5 text-warning">
                        <Star className="h-2.5 w-2.5 fill-warning" /> {lead.rating}
                      </span>
                    )}
                    {lead.niche && <span className="truncate">· {lead.niche}</span>}
                    {lead.location && <span className="truncate">· {lead.location.split('|')[0]}</span>}
                  </div>
                  {lead.emails && lead.emails[0] && (
                    <div className="truncate text-body-sm text-on-surface-variant">{lead.emails[0]}</div>
                  )}
                  <div className="mt-1 flex justify-end opacity-0 transition-opacity group-hover:opacity-100">
                    <select
                      value={lead.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => onStatusChange(lead.id, e.target.value)}
                      aria-label={`Move ${lead.name} to status`}
                      className="cursor-pointer rounded-card-sm border border-outline bg-surface px-1.5 py-0.5 text-body-sm capitalize text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="capitalize">
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
